import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import Percent from "_shared/percentages/percent";
import { Masked, MaskedFeeSchedule, MaskedTeam, MaskedContracts, MaskedDiscount } from "work/_shared/masked-fields";
import Proposal from "work/proposals/proposal";
import ProposalField, { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline, { RedlinedDates, RedlinedDocuments } from "work/proposals/redlining/proposal-redline";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import "work/proposals/__extensions/users/user-extensions";

export default class ProposalRedlineFactory {
  public static fromProposal(proposal: Proposal): ProposalRedline | undefined {
    if (!proposal.id || !proposal.name || !proposal.description) return;

    const currentUserIsLatestSubmittingParty = proposal.currentUser.isLatestSubmittingParty(proposal);
    const currentUserCanHire = proposal.currentUser.canHire(proposal);

    let originalVersion: Proposal = proposal.clone();
    let revisedVersion: Proposal = originalVersion;
    if (proposal.supersedes && !currentUserCanHire) {
      originalVersion = new Proposal({
        currentUser: proposal.currentUser,
        spec: proposal.supersedes,
        options: {
          withRedline: false,
        },
      });
      revisedVersion = currentUserIsLatestSubmittingParty ? originalVersion : proposal.clone();
    }

    let currentVersion: Proposal | undefined;
    if (currentUserCanHire || currentUserIsLatestSubmittingParty) {
      currentVersion = revisedVersion;
    }

    const sharedPartyRevision =
      !currentUserCanHire && currentUserIsLatestSubmittingParty && proposal.partyRevision
        ? new Proposal({
            currentUser: proposal.currentUser,
            spec: {
              ...proposal.partyRevision,
            },
            options: {
              withRedline: false,
            },
          })
        : undefined;

    if (sharedPartyRevision) {
      currentVersion = sharedPartyRevision;
    }

    const userRevision = proposal.userRevision
      ? new Proposal({
          currentUser: proposal.currentUser,
          spec: {
            ...proposal.userRevision,
          },
          options: {
            withRedline: false,
          },
        })
      : undefined;

    if (
      !currentUserCanHire &&
      userRevision &&
      (!currentVersion?.lastUpdated || !userRevision.lastUpdated || !currentVersion.lastUpdated.isAfter(userRevision.lastUpdated))
    ) {
      currentVersion = userRevision;
    }

    const nameRedline = new FieldRedline<ProjectName>({
      field: ProposalField.Name,
      originalEntry: originalVersion.name ?? null,
      revisedEntry: revisedVersion.name ?? null,
      currentEntry: currentVersion?.unresolvedFields.has(ProposalFieldName.Name) ? undefined : currentVersion?.name,
      diffWords: true,
    });

    const descriptionRedline = new FieldRedline<ProjectDescription>({
      field: ProposalField.Description,
      originalEntry: originalVersion.description ?? null,
      revisedEntry: revisedVersion.description ?? null,
      currentEntry: currentVersion?.unresolvedFields.has(ProposalFieldName.Description)
        ? undefined
        : currentVersion?.description,
      diffWords: true,
    });

    const feeScheduleRedline =
      Masked.isMasked(originalVersion?.feeSchedule) ||
      Masked.isMasked(revisedVersion.feeSchedule) ||
      Masked.isMasked(currentVersion?.feeSchedule)
        ? new MaskedFeeSchedule()
        : new FeeScheduleRedline(originalVersion.feeSchedule, revisedVersion.feeSchedule, currentVersion?.feeSchedule);

    const teamRestrictedRedline =
      Masked.isMasked(originalVersion.teamRestricted) ||
      Masked.isMasked(revisedVersion.teamRestricted) ||
      Masked.isMasked(currentVersion?.teamRestricted)
        ? new MaskedTeam()
        : new FieldRedline<AHBoolean>({
            field: ProposalField.TeamRestriction,
            originalEntry: originalVersion.teamRestricted ?? null,
            revisedEntry: revisedVersion.teamRestricted ?? null,
            currentEntry: currentVersion?.teamRestricted,
            diffWords: false,
          });

    const conflictsCheckWaivedRedline =
      Masked.isMasked(originalVersion.conflictsCheckWaived) ||
      Masked.isMasked(revisedVersion.conflictsCheckWaived) ||
      Masked.isMasked(currentVersion?.conflictsCheckWaived)
        ? new MaskedContracts()
        : new FieldRedline<AHBoolean>({
            field: ProposalField.WaiveConflictsCheck,
            originalEntry: originalVersion.conflictsCheckWaived,
            revisedEntry: revisedVersion.conflictsCheckWaived,
            currentEntry: currentVersion?.conflictsCheckWaived,
            diffWords: false,
          });

    const waivingConflictsChecks = Boolean(
      Masked.isNotMasked(conflictsCheckWaivedRedline) &&
      conflictsCheckWaivedRedline.isAccepted &&
      conflictsCheckWaivedRedline.currentEntry?.isEqualTo(new AHBoolean(true)),
    );

    const discountRedline =
      Masked.isMasked(originalVersion.discount) ||
      Masked.isMasked(revisedVersion.discount) ||
      Masked.isMasked(currentVersion?.discount)
        ? (originalVersion.discount as MaskedDiscount)
        : new FieldRedline<Percent>({
            field: ProposalField.Discount,
            originalEntry: originalVersion.discount ?? null,
            revisedEntry: revisedVersion.discount ?? null,
            currentEntry: currentVersion?.discount,
            diffWords: false,
          });

    return new ProposalRedline({
      name: nameRedline,
      description: descriptionRedline,
      ...this.createDatesRedline({ originalVersion, revisedVersion, currentVersion }),
      vendorTeam: this.createTeamRedline({ originalVersion, revisedVersion, currentVersion }),
      feeSchedule: feeScheduleRedline,
      conflictsCheckWaived: conflictsCheckWaivedRedline,
      teamRestricted: teamRestrictedRedline,
      ...this.createDocumentsRedline({
        originalVersion,
        revisedVersion,
        currentVersion,
        waivingConflictsChecks,
      }),
      discount: discountRedline,
      lastUpdated: new AHDate(),
    });
  }

  private static createDatesRedline(params: {
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
  }): RedlinedDates {
    const { originalVersion, revisedVersion, currentVersion } = params;
    let currentResponseDueBy: AHDate | null | undefined = currentVersion?.responseDueBy;
    if (currentVersion !== undefined && !currentVersion.responseDueBy && !originalVersion.responseDueBy) {
      currentResponseDueBy = null;
    }

    let currentStartDate: AHDate | null | undefined = currentVersion?.startDate;
    if (currentVersion !== undefined && !currentVersion.startDate && !originalVersion.startDate) {
      currentStartDate = null;
    }

    let currentEndDate: AHDate | null | undefined = currentVersion?.endDate;
    if (currentVersion !== undefined && !currentVersion.endDate && !originalVersion.endDate) {
      currentEndDate = null;
    }

    return {
      responseDueBy: new FieldRedline({
        field: ProposalField.ResponseDueBy,
        originalEntry: originalVersion.responseDueBy ?? null,
        revisedEntry: revisedVersion.responseDueBy ?? null,
        currentEntry: currentResponseDueBy,
        diffWords: false,
      }),
      startDate: new FieldRedline({
        field: ProposalField.StartDate,
        originalEntry: originalVersion.startDate ?? null,
        revisedEntry: revisedVersion.startDate ?? null,
        currentEntry: currentStartDate,
        diffWords: false,
      }),
      endDate: new FieldRedline({
        field: ProposalField.EndDate,
        originalEntry: originalVersion.endDate ?? null,
        revisedEntry: revisedVersion.endDate ?? null,
        currentEntry: currentEndDate,
        diffWords: false,
      }),
    };
  }

  private static createTeamRedline(params: {
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
  }): FieldRedlineArray<EntityMember> | MaskedTeam {
    const { originalVersion, revisedVersion, currentVersion } = params;
    const teamIsMasked =
      Masked.isMasked(originalVersion.vendorTeam) ||
      Masked.isMasked(revisedVersion.vendorTeam) ||
      Masked.isMasked(currentVersion?.vendorTeam);

    if (teamIsMasked) {
      return new MaskedTeam();
    }

    const originalEntries =
      originalVersion.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ?? [];
    const revisedEntries =
      revisedVersion.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ?? [];

    let currentEntries: EntityMember[] | undefined;
    if (currentVersion === undefined) {
      currentEntries = undefined;
    } else if (currentVersion.unresolvedFields.has(ProposalFieldName.Team)) {
      currentEntries = [];
    } else {
      currentEntries =
        currentVersion.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ?? [];
    }

    return FieldRedlineArray.create<EntityMember>({
      field: ProposalField.Team,
      entryField: ProposalField.TeamMember,
      originalEntries,
      revisedEntries,
      currentEntries,
    });
  }

  private static createDocumentsRedline(params: {
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
    waivingConflictsChecks: boolean;
  }): RedlinedDocuments {
    const { originalVersion, revisedVersion, currentVersion, waivingConflictsChecks } = params;
    const clientPoliciesMasked =
      Masked.isMasked(originalVersion?.clientPolicies) ||
      Masked.isMasked(revisedVersion.clientPolicies) ||
      Masked.isMasked(currentVersion?.clientPolicies);
    const vendorPoliciesMasked =
      Masked.isMasked(originalVersion?.vendorPolicies) ||
      Masked.isMasked(revisedVersion.vendorPolicies) ||
      Masked.isMasked(currentVersion?.vendorPolicies);
    const conflictsMasked =
      Masked.isMasked(originalVersion?.conflicts) ||
      Masked.isMasked(revisedVersion.conflicts) ||
      Masked.isMasked(currentVersion?.conflicts);

    let currentClientPolicies: WorkDocument[] | undefined;
    if (currentVersion === undefined) {
      currentClientPolicies = undefined;
    } else if (currentVersion.unresolvedFields.has(ProposalFieldName.ClientPolicies)) {
      currentClientPolicies = [];
    } else {
      currentClientPolicies = (currentVersion.clientPolicies as WorkDocument[]) ?? [];
    }

    let currentVendorPolicies: WorkDocument[] | undefined;
    if (currentVersion === undefined) {
      currentVendorPolicies = undefined;
    } else if (currentVersion.unresolvedFields.has(ProposalFieldName.VendorPolicies)) {
      currentVendorPolicies = [];
    } else {
      currentVendorPolicies = (currentVersion.vendorPolicies as WorkDocument[]) ?? [];
    }

    let currentConflicts: WorkDocument[] | undefined;
    if (currentVersion === undefined) {
      currentConflicts = undefined;
    } else if (currentVersion.unresolvedFields.has(ProposalFieldName.Conflicts)) {
      currentConflicts = [];
    } else {
      currentConflicts = (currentVersion.conflicts as WorkDocument[]) ?? [];
    }

    const documentsRedline = {
      clientPolicies: clientPoliciesMasked
        ? new MaskedContracts()
        : FieldRedlineArray.create<WorkDocument>({
            field: ProposalField.ClientPolicies,
            entryField: ProposalField.ClientPolicy,
            originalEntries: originalVersion.clientPolicies,
            revisedEntries: revisedVersion.clientPolicies,
            currentEntries: currentClientPolicies,
          }),
      vendorPolicies: vendorPoliciesMasked
        ? new MaskedContracts()
        : FieldRedlineArray.create<WorkDocument>({
            field: ProposalField.VendorPolicies,
            entryField: ProposalField.VendorPolicy,
            originalEntries: originalVersion.vendorPolicies,
            revisedEntries: revisedVersion.vendorPolicies,
            currentEntries: currentVendorPolicies,
          }),
      conflicts: conflictsMasked
        ? new MaskedContracts()
        : FieldRedlineArray.create<WorkDocument>({
            field: ProposalField.Conflicts,
            entryField: ProposalField.ConflictsDocument,
            originalEntries: originalVersion.conflicts,
            revisedEntries: revisedVersion.conflicts,
            currentEntries: currentConflicts,
          }),
    };

    if (waivingConflictsChecks && Masked.isNotMasked(documentsRedline.conflicts)) {
      documentsRedline.conflicts = documentsRedline.conflicts.acceptAll();
    }
    return documentsRedline;
  }
}
